import { IConfig } from '@qunhe/ktracker-types';
import merge from 'deepmerge';
import cookie from 'js-cookie';
import { blackList, DefTypes, EMPTY_USERID } from '../constants';

/**
 * 获取浏览器 UA 标识
 */
export function getUserAgent() {
  return window.navigator.userAgent;
}

/**
 * 当前用户浏览器的 UA 标识
 */
export const currentUserAgent = getUserAgent();

/**
 * 获取随机UUID
 */
export function getUUID() {
  let time = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (time + Math.random() * 16) % 16 | 0;
    time = Math.floor(time / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const currentVisitId = getUUID();

export function getCurrentTimestamp() {
  return new Date().getTime();
}

export function deepMerge(target: any, source: any): any {
  return merge(target, source);
}

export function getType(o: any): string {
  return Object.prototype.toString.call(o).slice(8, -1);
}

export function getInput(name: string): string | null {
  const metaData = document.querySelector(`meta[name="${name}"]`);
  if (metaData) {
    return metaData.getAttribute('content');
  }

  return '';
}

export function getServiceWithStage() {
  const metaData = document.querySelector(`meta[name="serviceWithStage"]`);
  if (metaData) {
    return metaData.getAttribute('content');
  }

  return 'other';
}

export function getCurrentUserId(): string | null {
  const userId = document.querySelector('meta[kjl-global="userid"]');
  if (userId) {
    return userId.getAttribute('content');
  }
  return '';
}

export function getReportUserId(options?: any): string {
  const currentUserId = getCurrentUserId();
  const isEmptyUserId = !currentUserId || EMPTY_USERID.includes(currentUserId);

  if (isEmptyUserId && options && ('deviceTokenKey' in options)) {
    return getDeviceToken(options.deviceTokenKey);
  }

  return currentUserId || '';
}

export function getLocation(): string | null {
  try {
    return window.location.href;
  } catch (e) {
    return null;
  }
}

/**
 * Generate a random 64-bit or 128-bit number in fixed-length hex format
 */
export function getTraceId(trace128bit?: boolean): string {
  const digits = '0123456789abcdef';
  const max = trace128bit ? 32 : 16;
  let n = '';

  for (let i = 0; i < max; i++) {
    const rand = Math.floor(Math.random() * 16);
    n += digits[rand];
  }

  return n;
}

/**
 * 获取浏览器路径参数
 * @param name 参数名
 * @param url 当前浏览器路径
 */
export function getParam(name: string, url?: string): string | null {
  if (!url) {
    url = window.location.href;
  }
  const index = url.indexOf('?');
  const result: any = (index !== -1 ? url.substring(index + 1) : url)
    .split('&')
    .reduce((params: { [key: string]: any }, param: string) => {
      const [key, value] = param.split('=');
      params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
      return params;
    }, { });
  return result[name] || '';
}

/**
 * 获取设备唯一ID
 *   如果不存在，就自动创建一个
 * @param name
 */
export function getDeviceToken(name: string) {
  let token = getCookie(name);
  if (!token) {
    token = getUUID();
    setCookie(name, token, 1000);
  }
  return token;
}

/**
 * 获取 Cookie
 * @param cname
 */
export function getCookie(cname: string): string {
  return cookie.get(cname) || '';
}

/**
 * 设置 Cookie
 * @param name
 * @param value
 * @param expires 天数 或者 时间
 */
export function setCookie(name: string, value: any, expires: any) {
  let date;
  switch (getType(expires)) {
    case DefTypes.DATE:
      date = expires;
      break;
    case DefTypes.NUMBER:
      date = new Date(new Date().getTime() + parseInt(expires, 10) * 1000 * 60 * 60 * 24);
      break;
    default:
      date = new Date();
      break;
  }

  cookie.set(name, value, { expires: date });
}

/**
 * 判断空对象
 * @param obj
 */
export function isEmptyObj(obj: any) {
  if (getType(obj) !== DefTypes.OBJECT) {
    return false;
  }

  return Object.keys(obj).length === 0;
}

/**
 * 格式化 URL
 * @param url
 */
export function formatUrl(url: string): string {
  const tempATag = document.createElement('a');
  tempATag.href = url;
  return tempATag.href;
}

/**
 * 获取URL Origin
 * @param url
 */
export function getOrigin(url: string) {
  const tempATag = document.createElement('a');
  tempATag.href = url;
  return tempATag.origin;
}

export const getDocOrigin = () => window.document.origin;

/**
 * 获取客户机 viewport 宽度
 */
export function getClientWidth() {
  return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
}

/**
 * 获取客户机 viewport 高度
 */
export function getClientHeight() {
  return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
}

/**
 * 合并默认配置与用户配置
 * @param data
 * @param options
 * @param labels
 * @param level
 */
export function assignDefaultData(data: any, options: IConfig, labels?: { [key: string]: string }, level?: string) {
  const g_labels: { [key: string]: string } = { };

  if (labels) {
    Object.keys(labels).forEach(key => {
      if (labels[key]) {
        g_labels[key] = labels[key];
      }
    });
  }

  return data
    ? Object.assign(data, {
      viewport: options.getViewport
        ? {
          width: getClientWidth(),
          height: getClientHeight(),
        }
        : { },
      ua: currentUserAgent,
      serviceWithStage: getServiceWithStage(),
      feBu: getInput('feBu'),
      feVersion: getInput('feVersion'),
      refer: window.document.referrer || '',
      url: getLocation(),
      browserId: getDeviceToken(options.deviceTokenKey),
      visitId: currentVisitId,
      // TODO 当获取不到 userId 时（以游客身份访问系统），用生成的 browserId 填充
      userId: getReportUserId(options),
      labels: g_labels,
      level,
    })
    : { };
}

/**
 * 时间格式化
 */
export function dateFormatter(str?: Date | number | string, fmt: string = 'hh:mm:ss') {
  let date: Date = new Date();
  if (!str) {
    date = new Date();
  }
  if (getType(str) === DefTypes.NUMBER) {
    date = new Date(new Date().setTime(str as number));
  } else if (getType(str) === DefTypes.STRING) {
    date = new Date(str as string);
  }
  if (date.toString() === 'Invalid Date') {
    date = new Date();
  }
  const o: any = {
    'M+' : date.getMonth() + 1,                      // 月份
    'd+' : date.getDate(),                           // 日
    'h+' : date.getHours(),                          // 小时
    'm+' : date.getMinutes(),                        // 分
    's+' : date.getSeconds(),                        // 秒
    'q+' : Math.floor((date.getMonth() + 3) / 3), // 季度
    'S'  : date.getMilliseconds(),                   // 毫秒
  };
  if (/(y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
  }
  for (const k in o) {
    if (o.hasOwnProperty(k) && new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1) ? (o[k]) : (('00' + o[k]).substr(('' + o[k]).length)));
    }
  }
  return fmt;
}

/**
 * 判断是否需要收集这个ajax url的性能
 * @param url
 * @param options
 */
export function checkIfNeedCollect(url: string | undefined | null, options: IConfig) {
  try {
    return !!url && [
      options.newExceptionApiUrl,
      options.exceptionApiUrl,
      options.eventApiUrl,
      options.newEventApiUrl,
      options.performanceApiUrl,
    ].concat(blackList).every(api => url.indexOf(api) === -1);
  } catch (e) {
    return false;
  }
}

/**
 * 判断是否可以对 ajax 或者 fetch 做性能收集
 * @param condition
 */
export function canIUseFeature(condition?: boolean) {
  const hasPMark = !!(window.performance && window.performance.mark);

  if (condition !== undefined) {
    return condition && hasPMark;
  }

  return hasPMark;
}

/**
 * JSON.stringify 增强版
 * @param json
 * @param defaultValue
 */
export function stringifyJSONIfPossible(json: any, defaultValue = json) {
  if (getType(json) === DefTypes.STRING) {
    return json;
  }

  try {
    return JSON.stringify(json) || defaultValue;
  } catch (e) {
    return defaultValue;
  }
}

export function parseJSONIfPossible<T>(json?: string, defaultValue?: T) {
  try {
    return JSON.parse(json!) as T || defaultValue;
  } catch (e) {
    return defaultValue;
  }
}

/**
 * 修正要上报的错误字段，发现 ErrorParser.parseStack 只能接受 Error 对象，传其他类型解析有问题
 * @param error
 */
export function fixReportError(error: any) {
  const dataType = getType(error);

  if (dataType === DefTypes.ERROR) {
    return error;
  }

  if (dataType === DefTypes.STRING) {
    return parseJSONIfPossible(error, error);
  }

  return stringifyJSONIfPossible(error, convertAnyErrorToString(error));
}

/**
 * 将任意类型的 error 转换成 string
 * @param error
 */
export function convertAnyErrorToString(error: any) {
  const dataType = getType(error);

  if (dataType === DefTypes.STRING) {
    return error;
  }

  if (dataType === DefTypes.OBJECT) {
    return stringifyJSONIfPossible(error, error.toString());
  }

  return error.toString();
}
